/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig';
import type { PDPComparisonAnalytics } from '@/types/analytics';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { type CertonaRecommendation } from '@/features/certona';

export type CMSSEOComparisonChartProps = {
  certonaRecommendations?: CertonaRecommendation[];
};

const LazyPDPComparisonTable = dynamic(
  () =>
    import('@/features/pdp/components/PDPComparisonTable/PDPComparisonTable').then(
      (mod) => mod.PDPComparisonTable,
    ),
  { ssr: true },
);

export function CMSSEOComparisonChart({ certonaRecommendations }: CMSSEOComparisonChartProps) {
  const preferredVehicle = usePreferredVehicle();
  const { acesEnabled } = useAcesFlags();
  const { data: productDetails } = useProductDetails();
  const { product } = productDetails ?? {};

  const { data: skuDetailsData } = useProductSkuDetails({
    skuIds: product?.itemId ? [product.itemId] : [],
    enabled: !!product?.itemId,
  });
  const skuDetails = skuDetailsData?.[0];

  const pdpComparisonEnabled = useFeatureFlag('PDP_COMPARISON_ENABLED') === 'true';

  const productId = acesEnabled
    ? (product?.partGroupId ?? product?.ecommProductId)
    : product?.ecommProductId;

  const { data: reviewStatisticsData } = useReviewStatistics({
    skuNumbers: product?.itemId ? [product.itemId] : [],
    enabled: Boolean(product?.itemId),
  });

  const { averageOverallRating, totalReviewCount } =
    (product?.itemId ? reviewStatisticsData?.[product?.itemId] : null) ?? {};

  const pdpComparisonAnalyticsData = useMemo(() => {
    if (product) {
      const {
        originalPartTypeId,
        brandName,
        subBrandName,
        ecommProductId,
        partGroupId,
        partTerminologyId,
      } = product;
      const data: PDPComparisonAnalytics = {
        originalPartTypeId: acesEnabled ? undefined : originalPartTypeId,
        brandName: subBrandName ?? brandName,
        repositoryId: acesEnabled ? undefined : ecommProductId,
        partGroupId: acesEnabled ? partGroupId : undefined,
        partTerminologyId: acesEnabled ? partTerminologyId : undefined,
      };

      return data;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  return pdpComparisonEnabled &&
    product &&
    productId &&
    product.brandName &&
    product.ecommProductId &&
    pdpComparisonAnalyticsData ? (
    <LazyPDPComparisonTable
      skuId={product.itemId}
      partNumber={product.partNumber}
      productName={product.itemDescription}
      price={
        skuDetails?.skuPricingAndAvailability?.corePrice
          ? skuDetails.skuPricingAndAvailability.retailPrice +
            skuDetails.skuPricingAndAvailability.corePrice
          : skuDetails?.skuPricingAndAvailability?.retailPrice
      }
      averageRating={averageOverallRating ? parseFloat(averageOverallRating) : undefined}
      totalReviews={totalReviewCount ? parseInt(totalReviewCount) : undefined}
      parentBrandName={product.brandName}
      subBrandName={product.subBrandName}
      productId={productId}
      productAttributes={product.productAttributes}
      location={product.location}
      itemWeight={product.itemWeight}
      warranty={skuDetails?.warranty}
      productImage={product.itemImageUrl ?? null}
      analyticsData={pdpComparisonAnalyticsData}
      currentSkuFitsVehicle={productDetails?.product?.vehicleFitFlag ?? false}
      currentSkuVehicleFitment={productDetails?.product?.vehicleFit}
      hasPreferredVehicle={!!preferredVehicle}
      isCurrentSkuAppProduct={product.recordType === 'app'}
      isCurrentSkuVehicleSpecific={product.vehicleSpecificFlag === true}
      certonaRecommendations={certonaRecommendations}
    />
  ) : null;
}
